// src/components/owners/OwnerNotesModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  X, Send, Search, Trash2, Bell, Clock, 
  User, Mail, Phone, Building2, MessageSquare, AtSign, AlertCircle
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const OwnerNotesModal = ({ owner, isOpen, onClose, onNoteAdded }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [mentionStaff, setMentionStaff] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [selectedNoteForDelete, setSelectedNoteForDelete] = useState(null);
  const [showStaffSuggestions, setShowStaffSuggestions] = useState(false);
  
  const noteInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && owner) {
      fetchNotes();
      fetchStaff();
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, owner]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      let url = `${API_BASE}/owners/${owner.id}/notes`;
      if (searchTerm) {
        url = `${API_BASE}/owners/${owner.id}/notes/search?q=${encodeURIComponent(searchTerm)}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let notesData = response.data;
      if (notesData.notes && Array.isArray(notesData.notes)) {
        setNotes(notesData.notes);
      } else if (Array.isArray(notesData)) {
        setNotes(notesData);
      } else {
        setNotes([]);
      }
    } catch (err) {
      console.error('Error fetching notes:', err);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE}/staff/?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableStaff(response.data);
    } catch (err) {
      console.error('Error fetching staff:', err);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_BASE}/owners/${owner.id}/notes`,
        {
          note: newNote,
          mention_staff_ids: mentionStaff
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewNote('');
      setMentionStaff([]);
      fetchNotes();
      if (onNoteAdded) onNoteAdded();
    } catch (err) {
      console.error('Error adding note:', err);
      alert('Failed to add note');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API_BASE}/owners/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedNoteForDelete(null);
      fetchNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Failed to delete note');
    }
  };

  const handleKeyPress = (e) => {
    if (e.ctrlKey && e.key === 'Enter') {
      handleAddNote();
    }
  };

  const handleNoteInputChange = (e) => {
    const value = e.target.value;
    setNewNote(value);
    
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1 && value.length - lastAtIndex < 30) {
      setShowStaffSuggestions(true);
    } else {
      setShowStaffSuggestions(false);
    }
  };

  const insertMention = (staff) => {
    const beforeAt = newNote.slice(0, newNote.lastIndexOf('@'));
    const newValue = `${beforeAt}@${staff.first_name} ${staff.last_name} `;
    setNewNote(newValue);
    if (!mentionStaff.includes(staff.id)) {
      setMentionStaff([...mentionStaff, staff.id]);
    }
    setShowStaffSuggestions(false);
    noteInputRef.current?.focus();
  };

  // Fonction pour formater la date avec heure complète
    const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    // Format: 15/04/2024 14:35
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

  if (!isOpen) return null;

  const displayNotes = notes;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-md bg-gray-900 shadow-2xl flex flex-col"
        style={{ 
          height: 'auto',
          minHeight: '500px',
          maxHeight: '85vh',
          margin: 'auto 0 auto auto',
          animation: 'slideIn 0.3s ease-out'
        }}
      >
        {/* Header */}
        <div className="border-b border-gray-800 bg-gray-900 px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare size={18} className="text-blue-400" />
              <div>
                <h2 className="text-sm font-semibold text-white">Activity & Notes</h2>
                <p className="text-xs text-gray-400 truncate max-w-[180px]">
                  {owner?.company_name || `${owner?.first_name} ${owner?.last_name}`}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Client Info */}
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <User size={14} className="text-gray-500" />
                <span className="text-gray-300">{owner?.first_name} {owner?.last_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 size={14} className="text-gray-500" />
                <span className="text-gray-300 truncate">{owner?.company_name || '—'}</span>
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <Mail size={14} className="text-gray-500 flex-shrink-0" />
                <a href={`mailto:${owner?.email}`} className="text-blue-400 hover:underline truncate text-sm">
                  {owner?.email}
                </a>
              </div>
            </div>
          </div>

          {/* Search bar */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchNotes()}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-9 pr-3 text-sm text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Notes list */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-300">Note History</h3>
              <span className="text-xs text-gray-500">{notes.length} notes</span>
            </div>

            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-gray-800/30 rounded-lg p-3 animate-pulse">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 bg-gray-700 rounded-full" />
                      <div className="h-2 bg-gray-700 rounded w-24" />
                      <div className="h-2 bg-gray-700 rounded w-16" />
                    </div>
                    <div className="h-3 bg-gray-700 rounded w-full" />
                  </div>
                ))}
              </div>
            ) : displayNotes.length === 0 ? (
              <div className="text-center py-8 bg-gray-800/30 rounded-lg">
                <Bell size={32} className="mx-auto mb-2 text-gray-600" />
                <p className="text-gray-500 text-sm">No notes yet</p>
                <p className="text-gray-600 text-xs mt-1">Add a note to keep track</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                {displayNotes.map((note) => (
                  <div key={note.id} className="group bg-gray-800/30 hover:bg-gray-800/50 rounded-lg p-3 transition-colors border border-gray-800 hover:border-gray-700">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <div className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <User size={12} className="text-blue-400" />
                          </div>
                          <span className="text-sm font-medium text-white">
                            {note.staff_name || note.staff_email?.split('@')[0] || 'Staff'}
                          </span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock size={10} />
                            {formatDateTime(note.created_at)}
                          </span>
                        </div>
                        <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap break-words mt-1">
                          {note.note}
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedNoteForDelete(note.id)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-500/20 rounded-lg transition-all text-gray-400 hover:text-red-400 flex-shrink-0"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Zone d'ajout de note */}
        <div className="border-t border-gray-800 bg-gray-900 p-4 flex-shrink-0">
          {/* Mentions */}
          {mentionStaff.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {mentionStaff.map((staffId) => {
                const staff = availableStaff.find(s => s.id === staffId);
                return staff ? (
                  <span key={staffId} className="inline-flex items-center gap-1 bg-blue-500/20 text-blue-400 text-xs px-2 py-0.5 rounded-full">
                    <AtSign size={9} />
                    {staff.first_name} {staff.last_name}
                    <button onClick={() => setMentionStaff(mentionStaff.filter(id => id !== staffId))} className="ml-0.5 hover:text-blue-300">×</button>
                  </span>
                ) : null;
              })}
            </div>
          )}

          {/* Textarea */}
          <div className="relative">
            <textarea
              ref={noteInputRef}
              value={newNote}
              onChange={handleNoteInputChange}
              onKeyDown={handleKeyPress}
              placeholder="Write a note... (@ to mention staff, Ctrl+Enter to send)"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2.5 text-sm text-white resize-none focus:outline-none focus:border-blue-500"
              rows={2}
            />
            
            {/* Suggestions @mention */}
            {showStaffSuggestions && availableStaff.length > 0 && (
              <div className="absolute bottom-full left-0 mb-1 w-56 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-20 max-h-40 overflow-y-auto">
                {availableStaff.map(staff => (
                  <button
                    key={staff.id}
                    onClick={() => insertMention(staff)}
                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-700 transition-colors flex items-center gap-2 text-gray-300"
                  >
                    <User size={12} className="text-gray-500" />
                    <span>{staff.first_name} {staff.last_name}</span>
                    <span className="text-xs text-gray-500 ml-auto">@{staff.email?.split('@')[0]}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Boutons */}
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors">
              Cancel
            </button>
            <button
              onClick={handleAddNote}
              disabled={!newNote.trim() || sending}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm flex items-center gap-1.5 disabled:opacity-50 transition-all"
            >
              {sending ? (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send size={14} />
              )}
              Add Note
            </button>
          </div>
        </div>

        {/* Modal confirmation suppression */}
        {selectedNoteForDelete && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
            <div className="bg-gray-800 rounded-lg max-w-xs w-full mx-4 p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle size={18} className="text-red-400" />
                <h3 className="text-base font-semibold text-white">Delete Note?</h3>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                This action cannot be undone. The note will be permanently deleted.
              </p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setSelectedNoteForDelete(null)} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white">
                  Cancel
                </button>
                <button onClick={() => handleDeleteNote(selectedNoteForDelete)} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm">
                  Delete Note
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default OwnerNotesModal;